import { Navigate, useLocation } from "react-router-dom"
import { useAuth } from "@/lib/auth"
import { useState, useEffect } from "react"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function checkAdmin() {
      if (!user?.id) {
        setIsAdmin(false)
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single()

        if (error) throw error
        setIsAdmin(data?.role === 'admin')
      } catch (error) {
        console.error('Error checking admin status:', error)
        setIsAdmin(false)
        // Show toast notification to inform user why access was denied
        toast.error('Unable to verify admin access. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    checkAdmin()
    // Re-check on each navigation so revoked admin role takes effect without re-login.
  }, [user?.id, location.pathname])

  // Not authenticated
  if (!user) {
    return <Navigate to="/auth/sign-in" state={{ from: location }} replace />
  }

  // Still checking admin status
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" role="status" aria-live="polite" aria-label="Checking admin access">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Not an admin
  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

